import apiInventry from '@/utils/api-inventry';
import { handleErrorResponse, handleResponse } from '@/utils/common/common';
import { PAGE_SIZE } from '@/utils/constant';
import { cleanPayload } from '@/utils/helper';
import { CommonApiError } from '@/utils/types/common';
import {
  FormListResponse,
  MenuFormPermissionsResponse,
  ResourceTypesResponse,
  UpdateFieldPermissionPayload,
  UpdateFieldPermissionResponse,
  UseGetFormFieldsParams,
} from '@/utils/types/permission';
import { PermissionPayload, PermissionResponse } from '@/utils/types/roles';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';
type GetAllFormListParams = {
  search?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  limit?: number;
};

// Hook to get form list with pagination
export const useGetFormList = (search?: string, sortBy = 'formNameLabel', sortDir = 'asc') => {
  return useInfiniteQuery<FormListResponse, Error>({
    queryKey: ['formList', search, sortBy, sortDir],
    queryFn: async ({ pageParam = 1 }) => {
      try {
        const response = await apiInventry.get<FormListResponse>('/access-security/permissions/form-list', {
          params: cleanPayload({
            sortBy,
            sortDir,
            limit: PAGE_SIZE,
            page: pageParam,
            ...(search && { search }),
          }),
        });
        return handleResponse(response);
      } catch (error) {
        if (axios.isAxiosError(error)) throw handleErrorResponse(error);
        throw error;
      }
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.pagination?.isNextPage ? (lastPage.pagination.currentPage ?? 0) + 1 : undefined;
    },
    staleTime: 0,
  });
};

export const useGetFormFields = ({
  dbFormName,
  roleId,
  sortBy = 'resourceLabel',
  sortDir = 'asc',
  page = 1,
  limit = 100,
  enabled = false,
  search = '',
}: UseGetFormFieldsParams) => {
  return useQuery({
    queryKey: ['formFields', dbFormName, roleId, sortBy, sortDir, page, limit, search],
    queryFn: async () => {
      const response = await apiInventry.get('/access-security/permissions/form-fields', {
        params: cleanPayload({
          dbFormName,
          roleId,
          sortBy,
          sortDir,
          page,
          limit,
          ...(search && { search }),
        }),
      });
      return handleResponse(response);
    },
    enabled,
    staleTime: 0,
  });
};

// Hook to get resource types (no pagination)
export const useGetResourceTypes = (dbFormName: string, enabled = false) => {
  return useQuery<ResourceTypesResponse, Error>({
    queryKey: ['resourceTypes', dbFormName],
    queryFn: async () => {
      try {
        const response = await apiInventry.get<ResourceTypesResponse>('/access-security/permissions/resource-types', {
          params: { dbFormName },
        });
        return handleResponse(response);
      } catch (error) {
        if (axios.isAxiosError(error)) throw handleErrorResponse(error);
        throw error;
      }
    },
    staleTime: 0,
    enabled,
  });
};

// Optional: Hook to update field permissions (you might need this later)
export const useUpdateFieldPermission = () => {
  return useMutation<UpdateFieldPermissionResponse, Error, UpdateFieldPermissionPayload>({
    mutationFn: async (payload) => {
      try {
        const response = await apiInventry.patch<UpdateFieldPermissionResponse>(
          '/access-security/permissions/role-permissions',
          payload,
        );
        return handleResponse(response);
      } catch (error) {
        if (axios.isAxiosError(error)) {
          throw handleErrorResponse(error);
        }
        throw error;
      }
    },
  });
};
// API function
export const getPermissionData = () => {
  return useMutation<PermissionResponse, CommonApiError, PermissionPayload>({
    mutationFn: async ({ dbFormName, resourceTab }: PermissionPayload) => {
      try {
        const response = await apiInventry.get<PermissionResponse>('/access-security/permissions/', {
          params: {
            formName: dbFormName,
            resourceTab,
          },
        });
        return handleResponse(response);
      } catch (error) {
        if (axios.isAxiosError(error)) throw handleErrorResponse(error);
        throw error;
      }
    },
  });
};

export const useGetMenuFormPermissions = () => {
  return useQuery<MenuFormPermissionsResponse, Error>({
    queryKey: ['menuPermissions'],
    queryFn: async () => {
      try {
        const response = await apiInventry.get<MenuFormPermissionsResponse>(
          '/access-security/permissions/form-permissions',
        );
        return handleResponse(response);
      } catch (error) {
        if (axios.isAxiosError(error)) throw handleErrorResponse(error);
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
    refetchOnMount: false, // Force refetch when component mounts
    refetchOnWindowFocus: false, // Don't refetch on window focus
  });
};
export const useGetAllFormList = (params: GetAllFormListParams) => {
  const { search, sortBy = 'formNameLabel', sortDir = 'asc', limit = PAGE_SIZE } = params;
  return useInfiniteQuery<FormListResponse, Error>({
    queryKey: ['allFormList', search, sortBy, sortDir],

    queryFn: async ({ pageParam = 1 }) => {
      try {
        const response = await apiInventry.get<FormListResponse>('/access-security/permissions/form-list', {
          params: cleanPayload({
            search,
            sortBy,
            sortDir,
            limit,
            page: pageParam,
          }),
        });
        return handleResponse(response);
      } catch (error) {
        if (axios.isAxiosError(error)) throw handleErrorResponse(error);
        throw error;
      }
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      return lastPage.pagination?.isNextPage ? (lastPage.pagination.currentPage ?? 0) + 1 : undefined;
    },
    staleTime: 0,
  });
};
